const inventoryTypes = new Set(["stocked", "composite"]);

const numberOr = (value, fallback) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const getCartQuantity = (product, cart = []) =>
  (Array.isArray(cart) ? cart : []).reduce(
    (quantity, item) =>
      item && String(item.id) === String(product.id)
        ? quantity + Math.max(numberOr(item.quantity, 0), 0)
        : quantity,
    0,
  );

const resolveLeafAvailability = (product = {}, context = {}) => {
  if (product.not_available) {
    return {
      status: "unavailable",
      available: false,
      canPreorder: false,
      reason: product.unavailable_reason || null,
    };
  }

  // Older compact API responses incorrectly mark bookings out_of_stock while
  // also reporting eligible slots. Never override location/manual restrictions.
  const compactBookingAvailable = product.type_of_product === "bookable" &&
    product.has_booking_slots === true && !product.booking_slots?.length &&
    product.unavailable_reason === "out_of_stock";
  if (product.available === false && !compactBookingAvailable) {
    const reason = product.unavailable_reason || null;
    const normalizedReason = String(reason || "").toLowerCase();
    return {
      status:
        normalizedReason.includes("stock") || normalizedReason.includes("inventory")
          ? "out-of-stock"
          : "unavailable",
      available: false,
      canPreorder: false,
      reason,
    };
  }

  const cartQuantity = getCartQuantity(product, context.cart);
  const maximum =
    product.max_addable_quantity === null ||
    product.max_addable_quantity === undefined
      ? Infinity
      : numberOr(product.max_addable_quantity, 0);
  if (maximum - cartQuantity <= 0) {
    return { status: "out-of-stock", available: false, canPreorder: false };
  }

  if (product.type_of_product === "bookable") {
    const slots = Array.isArray(product.booking_slots) ? product.booking_slots : [];
    const hasInventory = slots.some((slot) => numberOr(slot.inventory, 0) > 0);
    if (!hasInventory && !(slots.length === 0 && product.has_booking_slots)) {
      return { status: "out-of-stock", available: false, canPreorder: false };
    }
  }

  if (inventoryTypes.has(product.type_of_product)) {
    const increment = numberOr(product.increments, 1);
    if (increment <= 0) return { status: "out-of-stock", available: false, canPreorder: false };
    const minimum = Math.ceil(Math.max(numberOr(product.min_addable_quantity, 1), increment) / increment - 1e-9) * increment;
    const used = Math.max(
      numberOr(context.usedComponents && context.usedComponents[product.id], 0),
      0,
    );
    const components = Object.entries(product.components || {}).filter(([, component]) => component.type_of_product === "stocked");
    const inventory = product.type_of_product === "composite" && components.length
      ? Math.min(...components.map(([id, component]) =>
          (numberOr(component.inventory_on_hand, 0) - numberOr(context.usedComponents?.[id], 0)) / Math.max(numberOr(component.required_amount, 1), Number.EPSILON)))
      : numberOr(product.inventory_on_hand, 0) - used;
    if (maximum - cartQuantity < minimum) {
      return { status: "out-of-stock", available: false, canPreorder: false };
    }
    if (inventory < minimum - 1e-9) {
      const preorderedQuantity = (context.cart || []).filter(item =>
        String(item.id) === String(product.id) && (item.isPreordered === true || item.is_preordered === true)
      ).reduce((sum, item) => sum + numberOr(item.quantity, 0), 0);
      const canPreorder =
        product.allow_preordering === true &&
        numberOr(product.preorder_inventory, 0) - preorderedQuantity >= minimum - 1e-9;
      return canPreorder
        ? { status: "preorder", available: true, canPreorder: true }
        : { status: "out-of-stock", available: false, canPreorder: false };
    }
  }

  return { status: "available", available: true, canPreorder: false };
};

export const resolveProductAvailability = (product = {}, context = {}) => {
  const children = Array.isArray(product.children) ? product.children : [];
  if (product.has_variants && children.length) {
    const childAvailability = children.map((child) => ({
      product: child,
      ...resolveLeafAvailability(child, context),
    }));
    const availableChildren = childAvailability.filter((child) => child.available);
    const status = availableChildren.length
      ? availableChildren.some((child) => child.status === "available")
        ? "selection-required"
        : "preorder"
      : "out-of-stock";
    return {
      status,
      available: availableChildren.length > 0,
      selectionRequired: availableChildren.length > 0,
      canPreorder:
        availableChildren.length > 0 &&
        availableChildren.every((child) => child.canPreorder),
      children: childAvailability,
    };
  }
  const summaryValues = Array.isArray(product.variant_summary?.values)
    ? product.variant_summary.values
    : [];
  if (product.has_variants && summaryValues.length) {
    const available = summaryValues.some((value) => value.available !== false);
    return {
      status: available ? "selection-required" : "out-of-stock",
      available,
      selectionRequired: available,
      canPreorder: false,
      children: [],
    };
  }
  return {
    ...resolveLeafAvailability(product, context),
    selectionRequired: false,
    children: [],
  };
};

const getLocalizedVariantValue = (variantValue, language) =>
  language === "arabic"
    ? variantValue.variant_value_ar || variantValue.variant_value
    : variantValue.variant_value;

const getVariantKey = (variantValue) => variantValue.variant_key;

const getCompactVariantChild = (product = {}, childId) => {
  const values = Array.isArray(product.variant_summary?.values)
    ? product.variant_summary.values
    : [];
  for (const value of values) {
    const child = (Array.isArray(value.children) ? value.children : []).find(
      ({ id }) => String(id) === String(childId),
    );
    if (child) return child;
  }
  return null;
};

export const resolveVariantChildAvailability = (
  product = {},
  child = {},
  context = {},
) => {
  const availability = resolveProductAvailability(child, context);
  const compactChild = getCompactVariantChild(product, child.id);
  if (compactChild?.available === false) {
    return {
      ...availability,
      available: false,
      canPreorder: false,
      reason: compactChild.unavailable_reason || availability.reason || null,
      status:
        compactChild.unavailable_reason === "out_of_stock"
          ? "out-of-stock"
          : "unavailable",
    };
  }
  return availability;
};

export const getVariantDimensions = (product = {}) => {
  if (Array.isArray(product.variant_keys) && product.variant_keys.length) {
    return product.variant_keys.map((key) => ({
      key: key.variant_key,
      label: key.variant_key,
      labelAr: key.variant_key_ar || key.variant_key,
    }));
  }
  if (product.variant_summary?.primary_key) {
    const configured = product.primary_variant_key;
    const labelAr =
      configured && typeof configured === "object"
        ? configured.variant_key_ar
        : null;
    return [
      {
        key: product.variant_summary.primary_key,
        label: product.variant_summary.primary_key,
        labelAr: labelAr || product.variant_summary.primary_key,
      },
    ];
  }
  const firstChild = Array.isArray(product.children) ? product.children[0] : null;
  return (firstChild && Array.isArray(firstChild.variant_values)
    ? firstChild.variant_values
    : []
  ).map((value) => ({
    key: value.variant_key,
    label: value.variant_key,
    labelAr: value.variant_key_ar || value.variant_key,
  }));
};

const childMatchesSelection = (child, selection = {}) =>
  Object.entries(selection).every(([dimension, selectedValue]) =>
    (Array.isArray(child.variant_values) ? child.variant_values : []).some(
      (value) =>
        getVariantKey(value) === dimension &&
        (value.variant_value === selectedValue ||
          value.variant_value_ar === selectedValue),
    ),
  );

export const resolveVariantSelection = (product = {}, selection = {}, context = {}) => {
  const children = Array.isArray(product.children) ? product.children : [];
  const matchingChildren = children.filter((child) =>
    childMatchesSelection(child, selection),
  );
  const dimensions = getVariantDimensions(product);
  const complete = dimensions.every((dimension) => selection[dimension.key]);
  return {
    complete,
    matchingChildren,
    product: complete && matchingChildren.length === 1 ? matchingChildren[0] : null,
    availability:
      complete && matchingChildren.length === 1
        ? resolveVariantChildAvailability(product, matchingChildren[0], context)
        : null,
  };
};

export const getVariantChoices = (
  product = {},
  dimension,
  { language = "english", selection = {}, preserveSelection = false, ...context } = {},
) => {
  const children = Array.isArray(product.children) ? product.children : [];
  const choices = [];
  const byValue = new Map();
  children.forEach((child) => {
    const variantValue = (Array.isArray(child.variant_values)
      ? child.variant_values
      : []
    ).find((value) => getVariantKey(value) === dimension);
    if (!variantValue) return;
    const rawValue = variantValue.variant_value;
    if (!byValue.has(rawValue)) {
      const choice = {
        value: rawValue,
        label: getLocalizedVariantValue(variantValue, language),
        children: [],
      };
      byValue.set(rawValue, choice);
      choices.push(choice);
    }
    byValue.get(rawValue).children.push(child);
  });

  const otherSelection = { ...selection };
  delete otherSelection[dimension];
  return choices.map((choice) => {
    const compatibleChildren = choice.children.filter((child) =>
      childMatchesSelection(child, otherSelection),
    );
    const compatibleAvailability = compatibleChildren.map((child) => ({
      child,
      availability: resolveVariantChildAvailability(product, child, context),
    }));
    const compatibleAvailableChildren = compatibleAvailability.filter(
      ({ availability }) => availability.available,
    );
    const fallbackAvailability = choice.children.map((child) => ({
      child,
      availability: resolveVariantChildAvailability(product, child, context),
    }));
    const fallbackAvailableChildren = fallbackAvailability.filter(
      ({ availability }) => availability.available,
    );
    const compactSummaryValue =
      String(product.variant_summary?.primary_key || "").toLowerCase() ===
      String(dimension).toLowerCase()
        ? (Array.isArray(product.variant_summary?.values)
            ? product.variant_summary.values
            : []
          ).find(({ value }) => String(value) === String(choice.value))
        : null;
    const compactDefault = fallbackAvailableChildren.find(
      ({ child }) =>
        compactSummaryValue?.default_child_id !== null &&
        compactSummaryValue?.default_child_id !== undefined &&
        String(child.id) === String(compactSummaryValue.default_child_id),
    );
    // Preserve the shopper's other dimensions when that exact combination is
    // available. Otherwise use the same first valid configured child that a
    // chip activation resolves to. Availability, reason, and price must all be
    // derived from this one child or the chip can contradict its own action.
    const keepExactCombination = preserveSelection && Object.keys(otherSelection).length > 0;
    const availableChildren = keepExactCombination || compatibleAvailableChildren.length
      ? compatibleAvailableChildren
      : fallbackAvailableChildren;
    const availableProduct = keepExactCombination
      ? compatibleAvailableChildren[0]?.child
      : compatibleAvailableChildren.length
      ? compatibleAvailableChildren[0].child
      : compactDefault?.child || fallbackAvailableChildren[0]?.child;
    const fallbackProduct = compatibleChildren[0] || choice.children[0];
    const availabilityStatus = keepExactCombination && !compatibleChildren.length ? 'incompatible' : availableChildren.length
      ? availableChildren.some(
          ({ availability }) => availability.status === "available",
        )
        ? "available"
        : "preorder"
      : "out-of-stock";
    const unavailableReason = keepExactCombination && !compatibleChildren.length ? 'incompatible' : availableChildren.length
      ? null
      : (compatibleAvailability.length
          ? compatibleAvailability
          : fallbackAvailability
        ).find(({ availability }) => !availability.available)?.availability
          .reason || null;
    return {
      ...choice,
      available: availableChildren.length > 0,
      selectable: availableChildren.length > 0 || (keepExactCombination && compatibleChildren.length > 0),
      compatible: compatibleChildren.length > 0,
      availabilityStatus,
      unavailableReason,
      selected: selection[dimension] === choice.value,
      product: availableProduct || fallbackProduct,
      priceSummary: (availableProduct || fallbackProduct)?.price_summary || null,
    };
  });
};

export const getPrimaryVariantChoices = (product = {}, options = {}) => {
  const dimensions = getVariantDimensions(product);
  const configuredPrimary = product.primary_variant_key;
  const configuredKey =
    typeof configuredPrimary === "string"
      ? configuredPrimary
      : configuredPrimary &&
        (configuredPrimary.variant_key ||
          configuredPrimary.key ||
          configuredPrimary.name);
  const dimension = configuredKey
    ? dimensions.find(({ key }) => key === configuredKey) ||
      dimensions.find(
        ({ key }) =>
          String(key).toLowerCase() === String(configuredKey).toLowerCase(),
      ) ||
      dimensions[0]
    : dimensions[0];
  if (!dimension) return { dimension: null, choices: [] };
  const children = Array.isArray(product.children) ? product.children : [];
  const summaryValues = Array.isArray(product.variant_summary?.values)
    ? product.variant_summary.values
    : [];
  if (!children.length && summaryValues.length) {
    const language = options.language || "english";
    return {
      dimension,
      choices: summaryValues.map((value) => {
        const summaryChildren = Array.isArray(value.children)
          ? value.children
          : [];
        const pricedChild =
          summaryChildren.find(
            (child) => child.available !== false && child.price,
          ) || summaryChildren[0];
        const priceSummary =
          value.price_quote ||
          (pricedChild && pricedChild.price) ||
          (value.price !== null && value.price !== undefined
            ? {
                current: value.price,
                current_money: value.price_money || null,
                has_discount: false,
              }
            : null);
        const available = value.available !== false;
        return {
          value: value.value,
          label:
            language === "arabic"
              ? value.value_ar || value.value
              : value.value,
          available,
          compatible: true,
          availabilityStatus: available ? "available" : "out-of-stock",
          unavailableReason:
            !available
              ? value.unavailable_reason || pricedChild?.unavailable_reason || null
              : null,
          selected: options.selection?.[dimension.key] === value.value,
          product:
            value.product && typeof value.product === "object"
              ? value.product
              : null,
          priceSummary,
          childIds: value.child_ids || [],
          defaultChildId:
            value.default_child_id || (value.child_ids || [])[0] || null,
        };
      }),
    };
  }
  return {
    dimension,
    choices: getVariantChoices(product, dimension.key, options),
  };
};

const getSelectionFromChild = (child = {}) =>
  (Array.isArray(child.variant_values) ? child.variant_values : []).reduce(
    (selection, value) => {
      if (value.variant_key && value.variant_value) {
        selection[value.variant_key] = value.variant_value;
      }
      return selection;
    },
    {},
  );

export const resolveInitialVariantState = (product = {}, context = {}) => {
  if (!product.has_variants) {
    return {
      defaultChildId: null,
      priceSummary: null,
      product: null,
      selection: {},
    };
  }

  const summary = product.variant_summary || {};
  const children = Array.isArray(product.children) ? product.children : [];
  const summarySelection =
    summary.resolved_selection &&
    typeof summary.resolved_selection === "object" &&
    !Array.isArray(summary.resolved_selection)
      ? { ...summary.resolved_selection }
      : {};
  const summaryValues = Array.isArray(summary.values) ? summary.values : [];
  const summaryDefaultValue =
    summaryValues.find(
      ({ default_child_id }) =>
        String(default_child_id) === String(summary.default_child_id),
    ) ||
    summaryValues.find(
      ({ value }) => value === summarySelection[summary.primary_key],
    ) ||
    summaryValues.find(({ available }) => available !== false) ||
    summaryValues[0];
  const compactDefaultProduct =
    (summaryDefaultValue?.product &&
    typeof summaryDefaultValue.product === "object"
      ? summaryDefaultValue.product
      : null) ||
    (summary.product && typeof summary.product === "object"
      ? summary.product
      : null);
  let defaultChild = children.find(
    ({ id }) => String(id) === String(summary.default_child_id),
  );
  if (
    defaultChild &&
    !resolveVariantChildAvailability(product, defaultChild, context).available
  ) {
    defaultChild = null;
  }
  if (!defaultChild && Object.keys(summarySelection).length) {
    defaultChild = resolveVariantSelection(
      product,
      summarySelection,
      context,
    ).matchingChildren.find(
      (child) => resolveProductAvailability(child, context).available,
    );
  }
  if (!defaultChild && children.length) {
    defaultChild =
      children.find(
        (child) =>
          resolveVariantChildAvailability(product, child, context).available,
      ) || children[0];
  }

  const resolvedProduct = defaultChild || compactDefaultProduct;
  let selection = resolvedProduct
    ? getSelectionFromChild(resolvedProduct)
    : summarySelection;
  if (!Object.keys(selection).length && summary.primary_key) {
    if (summaryDefaultValue?.value) {
      selection = { [summary.primary_key]: summaryDefaultValue.value };
    }
  }

  const summaryValuePrice =
    summaryDefaultValue?.price_quote ||
    (summaryDefaultValue?.price !== null &&
    summaryDefaultValue?.price !== undefined
      ? {
          current: summaryDefaultValue.price,
          current_money: summaryDefaultValue.price_money || null,
          has_discount: false,
        }
      : null);

  return {
    defaultChildId:
      summary.default_child_id ||
      summaryDefaultValue?.default_child_id ||
      resolvedProduct?.id ||
      null,
    priceSummary:
      summary.price_quote ||
      summaryValuePrice ||
      resolvedProduct?.price_summary ||
      null,
    product: resolvedProduct || null,
    selection,
  };
};
